/**
 * The listing's date-range filter: named presets over local calendar dates.
 *
 * Pure and zone-explicit so it can be tested without a browser or a fake clock.
 * Everything here works in the **viewer's own** calendar, which is the right one
 * for this screen: the flat listing is not area-scoped, so there is no area
 * timezone to resolve against, and a reader picking "this weekend" means the
 * weekend on their own calendar. The area-relative rules in `week/week.ts` apply
 * to the week view, where the question is genuinely "what day is it *there*".
 *
 * Presets rather than two date pickers as the primary control: "the coming week"
 * and "last month" are how people actually ask, and a pair of pickers makes the
 * common case the slowest one. `CUSTOM` keeps the pickers available for the rest.
 */

/*
 * `isValidDate` is imported rather than restated: it is the same shape-plus-
 * calendar check the week view needs, and two copies would be two places for
 * `2026-02-30` to start being accepted. Re-exported so callers of this module get
 * their URL validator from the same place as everything else here.
 */
import { isValidDate } from "../week/week";

export { isValidDate };

/** Which window the listing is showing. */
export type RangePreset =
  "WEEK" | "MONTH" | "PAST_WEEK" | "PAST_MONTH" | "ALL" | "CUSTOM";

/** The default, and the reason this filter exists — see {@link resolveRange}. */
export const DEFAULT_PRESET: RangePreset = "WEEK";

/**
 * A resolved window as **inclusive local calendar dates**, `YYYY-MM-DD`.
 *
 * Either end may be absent, meaning open-ended — which is what `ALL` is, and what
 * a half-filled custom range is.
 */
export interface DateRange {
  from?: string;
  to?: string;
}

/** Labels for the control. Order is the order they render in. */
export const PRESET_LABELS: { preset: RangePreset; label: string }[] = [
  { preset: "WEEK", label: "Coming week" },
  { preset: "MONTH", label: "Coming month" },
  { preset: "PAST_WEEK", label: "Past week" },
  { preset: "PAST_MONTH", label: "Past month" },
  { preset: "ALL", label: "All dates" },
  { preset: "CUSTOM", label: "Custom" },
];

/**
 * A preset (plus any custom bounds) → the window it means.
 *
 * The forward presets start at **today**, not at "now": an event earlier this
 * evening is still today's, and starting the window at the current instant would
 * drop it from a range the user thinks of as including today. Whether it is *over*
 * is the separate past-events question, which the server answers against `endsAt`.
 *
 * The backward presets **end** today, symmetrically, and include it.
 *
 * `now` is a parameter rather than a `new Date()` inside, so this is testable and
 * so one render resolves every bound against the same moment.
 */
export function resolveRange(
  preset: RangePreset,
  custom: DateRange = {},
  now = new Date(),
): DateRange {
  const today = localDate(now);

  switch (preset) {
    case "WEEK":
      return { from: today, to: addDays(today, 7) };
    case "MONTH":
      return { from: today, to: addMonths(today, 1) };
    case "PAST_WEEK":
      return { from: addDays(today, -7), to: today };
    case "PAST_MONTH":
      return { from: addMonths(today, -1), to: today };
    case "ALL":
      return {};
    case "CUSTOM":
      return { from: custom.from, to: custom.to };
  }
}

/**
 * Does this window reach into the past?
 *
 * **This is what drives the automatic toggle.** A range whose start is before
 * today can only have been chosen to look at what already happened, so leaving
 * past events hidden would answer it with an empty list — the filter would look
 * broken rather than strict. The listing therefore turns the past-events toggle on
 * whenever this is true.
 *
 * Judged on `from` alone, and an open start counts: `ALL` and a custom range with
 * no lower bound both reach back indefinitely.
 *
 * `to` is deliberately not consulted. A window ending in the past necessarily
 * starts there too, and a *forward* window with a past end is a reversed range,
 * which the caller rejects rather than reinterprets.
 */
export function reachesPast(range: DateRange, now = new Date()): boolean {
  return range.from === undefined || range.from < localDate(now);
}

/**
 * Does this window's own start already exclude the past?
 *
 * **Why this exists as a separate question.** `startsFrom` and `upcomingOnly`
 * are two lower bounds on the same query, and the server applies both — so under
 * a forward range like the default `WEEK` (`from: today`), unticking "show past
 * events" changes nothing, because the range has already excluded everything
 * earlier. Measured against production: 12 rows either way.
 *
 * A checkbox that silently does nothing is worse than one that says it cannot:
 * the listing disables it here and explains that the range is what decides the
 * window. The route back to history is a past-reaching range — `PAST_WEEK`,
 * `PAST_MONTH`, `ALL` — which is the control that actually moves the bound.
 *
 * The exact complement of {@link reachesPast}, and deliberately written as its
 * own function rather than a `!` at the call site: the two drive opposite UI
 * states from the same fact, and a bare negation reads as "not forced" rather
 * than "the range settles it".
 */
export function excludesPast(range: DateRange, now = new Date()): boolean {
  return !reachesPast(range, now);
}

/**
 * A resolved window → the instants the server filters on.
 *
 * Two conversions happen here, and both are the point of the function:
 *
 *   1. **Local date → instant**, using the viewer's zone. The server's
 *      `startsFrom`/`startsBefore` take instants precisely so this choice is made
 *      somewhere that knows the zone, rather than being assumed to be UTC.
 *   2. **Inclusive `to` → exclusive bound**, by advancing to the next local
 *      midnight. An inclusive end-of-day would need 23:59:59.999… and picking a
 *      precision there is how a 23:59:59.7 event goes missing — the same reasoning
 *      as the server's local-date upper bound.
 */
export function toInstants(range: DateRange): {
  startsFrom?: string;
  startsBefore?: string;
} {
  return {
    startsFrom: range.from
      ? startOfLocalDay(range.from).toISOString()
      : undefined,
    startsBefore: range.to
      ? startOfLocalDay(addDays(range.to, 1)).toISOString()
      : undefined,
  };
}

/** Today in the viewer's own zone, `YYYY-MM-DD`. */
export function localDate(instant: Date): string {
  // en-CA formats as YYYY-MM-DD, which is the shape everything here works in.
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(instant);
}

/**
 * Local midnight on a calendar date, as an instant.
 *
 * Built by *asking* the platform rather than by constructing a string with an
 * offset: `new Date("2026-08-17T00:00:00")` is parsed as local time by every
 * current engine, which is exactly what is wanted and needs no offset arithmetic
 * that would be wrong on a DST transition day.
 *
 * Exported because it is also **the calendar boundary**: `react-day-picker` works
 * in `Date` objects while everything in this module works in zoneless
 * `YYYY-MM-DD` strings, and this pair — with {@link localDate} going the other way
 * — is where the two meet. Keeping the conversion here rather than in the
 * component means the picker cannot introduce a second, subtly different idea of
 * what "a day" is; a naive `new Date(dateString)` in a component would parse as
 * **UTC** midnight and shift the selected day for every viewer west of UTC.
 */
export function startOfLocalDay(date: string): Date {
  return new Date(`${date}T00:00:00`);
}

/**
 * Calendar-date arithmetic, in UTC deliberately.
 *
 * These are bare dates with no zone attached, and stepping a *local* midnight by
 * 24 hours lands on the same day again across a spring-forward. The zone enters
 * once, in {@link startOfLocalDay}.
 */
function addDays(date: string, days: number): string {
  const cursor = parseDate(date);
  cursor.setUTCDate(cursor.getUTCDate() + days);
  return format(cursor);
}

/**
 * Months, clamped to the end of a short target month.
 *
 * `setUTCMonth` alone would roll 31 January + 1 month to 3 March, so "the coming
 * month" from 31 January would silently include two extra days. Clamping to 28
 * February is what a reader means.
 */
function addMonths(date: string, months: number): string {
  const cursor = parseDate(date);
  const day = cursor.getUTCDate();

  cursor.setUTCDate(1);
  cursor.setUTCMonth(cursor.getUTCMonth() + months);

  const lastDayOfTarget = new Date(
    Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 0),
  ).getUTCDate();

  cursor.setUTCDate(Math.min(day, lastDayOfTarget));
  return format(cursor);
}

function parseDate(date: string): Date {
  if (!isValidDate(date)) {
    throw new Error(`Expected YYYY-MM-DD, got "${date}"`);
  }
  return new Date(`${date}T00:00:00Z`);
}

function format(date: Date): string {
  return date.toISOString().slice(0, 10);
}
